import _ from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { openModalAction } from '../../../actions/modal';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { getFiltersDefinition } from '../../../components/AdvancedFilters/FiltersDefinition';
import { getFiltersForRequest, urlInjected } from '../../../components/AdvancedFilters/helpers';
import { PageHeader } from '../../../components/PageHeader';
import { TextInput } from '../../../components/PageHeader/TextInput';
import { DATE_FORMAT_YEAR_MONTH_DAY, DATE_MONTH_DAY_YEAR } from '../../../constants';
import Strings from '../../../Strings';
import { downloadFileOnly, showStatus } from '../../../utils';
import {  CUSTOM, DateFilters } from '../../../utils/DateFilters';
import './Billings.scss';
import { actions } from './redux/actions';
import { GET_TASK_BILLING_RESULT, UPDATE_TASK_STATUS_ERROR } from './redux/constants';
import DropdownMenu from '../../../components/DropdownMenu/DropdownMenu';
import OrgSelect from '../../../components/OrgSelect/OrgSelect';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import { ActionDropdownSingleChoiceMenu, ActionWidget } from '../../../components/ActionWidget';
import { useGlobalPageSize } from '../../../utils/tableUtils';
import Select from '../../../components/Select';

const PAGE_LIMIT = 10;

function Billings(props) {
  const { orgIds, orgs, tasks, filters } = props;
  const { getBilling, updateStatus, downloadBillingTaskData, downloadBillingSummaryData } = props;
  const { openDateRangeSelectionModal, showNotification, onNavigate } = props;
  const globalPageSize = useGlobalPageSize();
  const pageRequest = React.useRef({
    offset: 0,
    search: '',
    limit: globalPageSize,
    startDate: '',
    endDate: '',
    orgId: (orgIds && orgIds.length > 0) ? orgIds : undefined,
  });
  const [data, setData] = React.useState([]);
  const [selectedTasks, setSelectedTasks] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [pagination, setPagination] = React.useState({});
  const tableRef = React.useRef(null);

  const onDateSubmited = dates => {
    pageRequest.current.startDate = dates.startDate ? moment(dates.startDate * 1000).format(DATE_FORMAT_YEAR_MONTH_DAY) : undefined;
    pageRequest.current.endDate = dates.endDate ? moment(dates.endDate * 1000).format(DATE_FORMAT_YEAR_MONTH_DAY) : undefined;
    pageRequest.current.offset = 0;
    loadData(true, true);
  };

  const onDataReceived = (resp, clearDataBeforeFetch = false, showDateRangeModalIfNoData = false) => {
    const newPagination = resp.response?.pagination || {};
    if (newPagination.totalRecords === 0 && showDateRangeModalIfNoData) {
      openDateRangeSelectionModal({
        actionOnDone: onDateSubmited,
        text: Strings.noDataInGivenPeriod,
        startDate: pageRequest.current.startDate ? moment(pageRequest.current.startDate * 1000) : undefined,
        endDate: pageRequest.current.endDate ? moment(pageRequest.current.endDate * 1000) : undefined,
      });
    }
    if (resp.type === GET_TASK_BILLING_RESULT) {
      setData(resp.response.data || []);
      setPagination(newPagination);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };

  const loadData = (clearDataBeforeFetch, showDateRangeModalIfNoData) => {
    if (Number.isNaN(pageRequest.current.limit) || pageRequest.current.limit <= 0) {
      return;
    }
    if (clearDataBeforeFetch) {
      setData([]);
      setPagination({});
    }
    setIsLoading(true);
    getBilling(pageRequest.current).then(resp => {
      onDataReceived(resp, clearDataBeforeFetch, showDateRangeModalIfNoData);
    });
    tableRef?.current?.resetSelectedRows();
  };

  const onDateRangeChanged = dateRange => {
    if (dateRange.value === CUSTOM) {
      openDateRangeSelectionModal({ actionOnDone: onDateSubmited });
    } else {
      if (dateRange) {
        pageRequest.current.startDate = moment(dateRange.dates.startDate()).format(DATE_FORMAT_YEAR_MONTH_DAY);
        pageRequest.current.endDate = moment(dateRange.dates.endDate()).format(DATE_FORMAT_YEAR_MONTH_DAY);
      } else {
        pageRequest.current.startDate = '';
        pageRequest.current.endDate = '';
      }
      pageRequest.current.offset = 0;
      loadData(true);
    }
  };

  const getSecondHeaderComponents = () => {
    const actionsButton = (
      <DropdownMenu
        title={Strings.actions}
        options={[
          { onSelect: onDownload, label: Strings.downloadEvents },
          { onSelect: onDownloadSummary, label: Strings.downloadSummary },
        ]}
      />
    );

    return (
      <>
        <TextInput className="search" placeholder={Strings.search} onChange={onTextInputChange} />
        <div data-tooltip-id="tooltip" data-tooltip-content={Strings.billing.reportDateRangeTooltip}>
          <Select
            id="billing-date-range-filter"
            label={Strings.dateRange}
            data={[
              DateFilters.Custom,
              DateFilters.ThisMonth,
              DateFilters.Last30Days,
              DateFilters.LastMonth,
              DateFilters.Last6Months,
              DateFilters.ThisYear,
              DateFilters.Last12Months,
              DateFilters.AllTime,
            ]}
            onChange={onDateRangeChanged}
            defaultValue={DateFilters.AllTime.value}
          />
        </div>
        <AdvancedFilters>
          <Filter definition={getFiltersDefinition().billingStatus} />
        </AdvancedFilters>
        {actionsButton}
      </>
    );
  }

  const onDownload = () => {
    const onDateSubmited = dates => {
      const request = {
        startDate: dates.startDate ? moment(dates.startDate * 1000).format(DATE_FORMAT_YEAR_MONTH_DAY) : undefined,
        endDate: dates.endDate ? moment(dates.endDate * 1000).format(DATE_FORMAT_YEAR_MONTH_DAY) : undefined,
        orgId: pageRequest.current.orgId,
      };
      downloadBillingTaskData(request)
        .then(resp =>
          onDownloadBtnClickSuccessHandler(resp, `BillEvents_${request.startDate || ''}_${request.endDate || ''}`),
        );
    };
    openDateRangeSelectionModal({
      actionOnDone: onDateSubmited,
      title: Strings.downloadEvents,
      submitButtonText: Strings.download,
      maxRangeAllowedMonths: 6,
      startDate: pageRequest.current.startDate ? moment(pageRequest.current.startDate) : undefined,
      endDate: pageRequest.current.endDate ? moment(pageRequest.current.endDate) : undefined,
      dateRangeInformation: Strings.billing.reportDateRangeTooltip,
    });
  };

  const onDownloadSummary = () => {
    const onDateSubmited = dates => {
      const request = {
        startCalendar: dates.startDate ? moment(dates.startDate * 1000).format(DATE_FORMAT_YEAR_MONTH_DAY) : undefined,
        endCalendar: dates.endDate ? moment(dates.endDate * 1000).format(DATE_FORMAT_YEAR_MONTH_DAY) : undefined,
        orgId: pageRequest.current.orgId,
        ...(dates.showAllColumns ? { showAllColumns: true } : {}),
      };
      downloadBillingSummaryData(request)
        .then(resp =>
          onDownloadBtnClickSuccessHandler(resp, `BillSummaryAll_${request.startCalendar || ''}_${request.endCalendar || ''}`),
        );
    };
    openDateRangeSelectionModal({
      actionOnDone: onDateSubmited,
      title: Strings.downloadSummary,
      submitButtonText: Strings.download,
      maxRangeAllowedMonths: 6,
      startDate: pageRequest.current.startDate ? moment(pageRequest.current.startDate) : undefined,
      endDate: pageRequest.current.endDate ? moment(pageRequest.current.endDate) : undefined,
      showAllColumnsToggle: true,
      showAllColumnsToggleLabel: Strings.includeEmptyDataInExport,
      dateRangeInformation: Strings.billing.reportDateRangeTooltip,
    });
  };

  const onDownloadBtnClickSuccessHandler = (req, filename) => {
    const fileName =
      req?.response && req?.response?.name ? req?.response?.name : filename ? filename : moment().toString();
    downloadFileOnly(req, fileName);
  };

  React.useEffect(() => {
    if (!urlInjected(window.location.search)) {
      pageRequest.current.orgId = (orgIds && orgIds.length > 0) ? orgIds : undefined;
      pageRequest.current.offset = 0;
      loadData(true);
    }
  }, [orgIds]);

  React.useEffect(() => {
    let newFiltersForRequest = getFiltersForRequest(filters);
    if (newFiltersForRequest?.length === 0) {
      newFiltersForRequest = undefined;
    }
    if (_.isEqual(newFiltersForRequest, pageRequest.current.filters)) {
      return;
    }
    pageRequest.current.filters = newFiltersForRequest;
    pageRequest.current.offset = 0;
    loadData(true);
  }, [filters]);


  const onPageSizeChange = limit => {
    pageRequest.current.limit = limit;
    pageRequest.current.offset = 0;
    loadData(true);
  };

  const onOffsetChange = (offset, limit) => {
    if (offset === pageRequest.current.offset && limit === pageRequest.current.limit) {
      return;
    }
    pageRequest.current.offset = offset;
    if (limit) {
      pageRequest.current.limit = limit;
    }
    loadData();
  };

  const onTextInputChange = e => {
    const query = e.target.value;
    return onSearchQueryChangeDebounced(query);
  };

  const onSearchQueryChangeDebounced = _.debounce(query => {
    pageRequest.current.offset = 0;
    pageRequest.current.search = query;
    loadData(true);
  }, 1000);

  const onSortClick = sorting => {
    const sortColumn = sorting?.length > 0 ? sorting[0].id : undefined;
    const sortType = sorting?.length > 0 ? (sorting[0].desc ? 'descending' : 'ascending') : undefined;
    if (sortColumn === pageRequest.current.sortColumn && sortType === pageRequest.current.sortType) {
      return;
    }
    pageRequest.current.offset = 0;
    pageRequest.current.sortColumn = sortColumn;
    pageRequest.current.sortType = sortType;
    loadData(true);
  };

  const onRowSelected = task => {
    if (task?.id) {
      onNavigate(`/billings/${task.id}`);
    }
  };

  const onStatusUpdate = async value => {
    const taksToUpdate = selectedTasks.filter(t => t.status !== value);
    await Promise.all(
      taksToUpdate.map(async t => {
        const response = await updateStatus({ id: t.id, status: value });
        if (response && response.type === UPDATE_TASK_STATUS_ERROR) {
          showNotification(response.response?.data?.error?.message, 5000, true);
        }
      }),
    );
    loadData(true);
  };

  const columns = React.useMemo(() => {
    const ret = [
      columnHelper.accessor('pharmacyPatientID', {
        cell: info => info.getValue() || '-',
        header: Strings.capPatient.patientId,
        size: 250,
        enableSorting: false,
      }),

      columnHelper.accessor('patientName', {
        cell: info => info.getValue() || '-',
        header: Strings.capPatient.patientName,
        size: 250,
        enableSorting: false,
      }),
    ];

    if (orgs?.length > 1) {
      ret.push(columnHelper.accessor('organization', {
        header: Strings.organization,
        cell: info => info.getValue() || '-',
        size: 200,
        enableSorting: false,
      }));
    }

    ret.push(
      columnHelper.accessor('patientDOB', {
        cell: info => (info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-'),
        header: Strings.capPatient.dob,
        size: 250,
        enableSorting: false,
      })
    );

    ret.push(
      columnHelper.accessor('startDate', {
        cell: info => (info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-'),
        header: Strings.capPatient.startDate,
        size: 250,
        enableSorting: false,
      })
    );
    ret.push(
      columnHelper.accessor('endDate', {
        id: 'end_date',
        cell: info => (info.getValue() ? moment(info.getValue()).format(DATE_MONTH_DAY_YEAR) : '-'),
        header: Strings.capPatient.endDate,
        size: 250,
        enableSorting: true,
      })
    );

    ret.push(
      columnHelper.accessor('cptCode', {
        cell: info => (info.getValue() || '-'),
        header: Strings.capPatient.cptCodes,
        size: 250,
      })
    );

    ret.push(
      columnHelper.accessor('status', {
        cell: info => (info.getValue() ? showStatus(info.getValue()) : '-'),
        header: Strings.capPatient.status,
        size: 250,
      })
    );
    return ret;
  }, []);

  const headerTitle =
    !isLoading && tasks && pagination?.totalRecords
      ? Strings.formatString(Strings.showingX, pagination.totalRecords, Strings.billing.tasks)
      : Strings.showingWait;

  return (
    <div className="billing-container">
      <PageHeader left={Strings.billing.billing} right={<OrgSelect />} />
      <PageHeader left={headerTitle} right={() => getSecondHeaderComponents()} />
      <AdvancedFiltersBar />
      <Table3
        name={`billing-table-${orgs?.length}`}
        isLoading={isLoading && data.length === 0}
        data={data || []}
        columns={columns}
        pagination={pagination}
        onRowClick={onRowSelected}
        onOffsetChange={onOffsetChange}
        onSortingChange={onSortClick}
        onPageSizeChange={onPageSizeChange}
        onRowSelectionChange={setSelectedTasks}
        enableColumnFiltering
        enableRowSelection
        pinFirstColumn
        ref={tableRef}
      />
      <ActionWidget show={selectedTasks?.length > 0}>
        <ActionDropdownSingleChoiceMenu
          img="action-edit"
          tooltiptext={Strings.changeStatus}
          options={[
            {
              value: 'submitted',
              text: Strings.billingStatus.submitted,
              isDisabled: selectedTasks.every(task => task.status === 'submitted'),
            },
            {
              value: 'onhold',
              text: Strings.billingStatus.onhold,
              isDisabled: selectedTasks.every(task => task.status === 'onhold'),
            },
            {
              value: 'pending',
              text: Strings.billingStatus.pending,
              isDisabled: selectedTasks.every(task => task.status === 'pending'),
            },
          ]}
          action={onStatusUpdate}
        />
      </ActionWidget>
    </div>
  );
}

Billings.propTypes = {
  downloadBillingTaskData: PropTypes.func,
  downloadBillingSummaryData: PropTypes.func,
  getBilling: PropTypes.func,
  isLoading: PropTypes.any,
  onNavigate: PropTypes.func,
  pagination: PropTypes.shape({
    offset: PropTypes.number,
    total: PropTypes.number,
  }),
  tasks: PropTypes.array,
  updateStatus: PropTypes.func,
  openDateRangeSelectionModal: PropTypes.func,
  orgIds: PropTypes.array,
  filters: PropTypes.array,
  openConfirmModal: PropTypes.func,
  orgs: PropTypes.array,
  showNotification: PropTypes.func,
};

const mapStateToProps = state => {
  const { careBilling } = state.superUser;
  return {
    role: state.auth.role,
    tasks: careBilling?.tasks,
    pagination: careBilling?.pagination,
    isLoading: careBilling?.isLoading,
    filters: state.entities.advancedFilters.filters.items,
    orgIds: state.entities?.orgFilter?.orgIds,
    orgs: state.entities?.orgFilter?.orgs,
  };
};

const mapDispatchToProps = dispatch => ({
  updateStatus: data => dispatch(actions.updateTaskStatus(data)),
  onNavigate: path => dispatch(push(path)),
  getBilling: pageRequest => dispatch(actions.getBillingTaskList(pageRequest)),
  getDeviceFilter: () => dispatch(actions.getDeviceFilter()),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  downloadBillingTaskData: request => dispatch(actions.downloadBillingTaskData(request)),
  downloadBillingSummaryData: request => dispatch(actions.downloadBillingSummaryData(request)),
  openDateRangeSelectionModal: data => dispatch(openModalAction('data-range-selection-modalion', data)),
  showNotification: (message, duration, isError) =>
    dispatch(
      openModalAction('notification-modal', {
        message,
        duration,
        isError,
      })
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Billings);
